import React, { useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { useAppSelector } from '../../redux/hooks';

interface ApiKeyDisplayProps {
  label?: string;
  value?: string;
  showCopy?: boolean;
  helpText?: string;
}

export const ApiKeyDisplay: React.FC<ApiKeyDisplayProps> = ({
  label = 'Your API Key:',
  value,
  showCopy = true,
  helpText,
}) => {
  const { currentApiKey } = useAppSelector((state) => state.auth);
  const displayValue = value ?? currentApiKey;

  const [copied, setCopied] = useState(false);

  const copyToClipboard = useCallback(async () => {
    if (!displayValue) return;

    try {
      await navigator.clipboard.writeText(displayValue);
      toast.success('Copied!');
      setCopied(true);

      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy');
    }
  }, [displayValue]);

  if (!displayValue) return null;

  return (
    <div className="mt-4 w-full bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-xl p-4 transition-all duration-300 hover:shadow-md">
      <label className="text-xs text-[var(--text-muted)] block mb-2 tracking-wide">
        {label}
      </label>

      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <code className="flex-1 px-3 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg text-sm font-mono text-[var(--text-primary)] break-all transition-colors">
          {displayValue}
        </code>

        {showCopy && (
          <button
            onClick={copyToClipboard}
            aria-label="Copy API key"
            className="flex items-center justify-center px-3 py-2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-tertiary)] hover:bg-[var(--accent-color)] hover:text-white active:scale-95 transition-all duration-200 ease-in-out min-w-[42px]"
          >
            <span className="relative flex items-center justify-center w-5 h-5 transition-transform duration-300">
              {copied ? (
                <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              )}
            </span>
          </button>
        )}
      </div>

      {helpText && (
        <p className="text-xs text-[var(--text-muted)] mt-3 leading-relaxed">
          {helpText}
        </p>
      )}
    </div>
  );
};
